import { CloudinaryImage } from "@cloudinary/url-gen";
import { fill } from "@cloudinary/url-gen/actions/resize";
import { autoGravity, compass } from "@cloudinary/url-gen/qualifiers/gravity";

const BreakPoints = [1600, 1200, 768, 576];

const createCloudinaryImage = (public_id, baseUrl = "assets.vacationvaults.ca") =>
	new CloudinaryImage(
		public_id,
		{ cloudName: "dtnwfag6s" },
		{
			privateCdn: true,
			secureDistribution: baseUrl,
			secure: true,
			analytics: false,
		}
	);

const calculateDimensions = (image, maxWidth, transformations, _aspectRatio) => {
	let width;
	let height;
	let aspectRatio;

	try {
		if (transformations) {
			width = transformations.match(/w_[0-9]*/g);
			height = transformations.match(/h_[0-9]*/g);

			// If we're given a width in the transformations, we should set the image to that.
			// Else we will use the images native dimensions to calculate its aspect ratio.
			if (width) {
				width = parseInt(width[0].substring(2), 10);

				if (height) {
					height = parseInt(height[0].substring(2), 10);
					aspectRatio = width / height;
				} else {
					aspectRatio = image.width / image.height;
					if (width > maxWidth) width = maxWidth;
				}
			} else {
				// Width should never be larger than the provided maxWidth
				width = image.width >= maxWidth ? maxWidth : image.width;
				aspectRatio = image.width / image.height;
				height = width * aspectRatio;

				// Set custom aspectRatios for named transformations
				if (image.raw_transformation) {
					if (image.raw_transformation.includes("WJV-Full-Container")) {
						aspectRatio = 16 / 5;
					} else if (image.raw_transformation.includes("WJV-Split-Container")) {
						aspectRatio = 16 / 9;
					} else if (image.raw_transformation.includes("WJV-2-3-Container")) {
						aspectRatio = 16 / 9;
					}
				}
			}
		} else {
			width = image.width >= maxWidth ? maxWidth : image.width;
			aspectRatio = image.width / image.height;
			height = width * aspectRatio;
		}

		if (_aspectRatio) {
			width = image.width >= maxWidth ? maxWidth : image.width;
			aspectRatio = _aspectRatio;
			height = width * aspectRatio;
		}

		height = parseInt(width / aspectRatio, 10);
	} catch (error) {
		width = 800;
		height = 450;
		aspectRatio = 16 / 9;
	}

	return {
		height,
		width,
		aspectRatio,
	};
};

const generateSizeString = (index, arr = [], maxWidth) => {
	if (index >= BreakPoints.length) {
		return arr.join();
	}

	const transformWidth = BreakPoints[index];
	const sizeArr = arr;

	if (maxWidth <= BreakPoints[index + 1]) {
		return generateSizeString(index + 1, sizeArr, maxWidth);
	}

	if (index === 0) {
		sizeArr.push(`(max-width: ${transformWidth}px) ${transformWidth}w`);
	} else {
		sizeArr.push(
			` (min-width: ${Math.round(
				transformWidth / 2
			)}px) and (max-width: ${transformWidth}px) ${transformWidth}w`
		);
	}

	return generateSizeString(index + 1, sizeArr, maxWidth);
};

const configureGravity = (resize, disableAutoGravity, transformation) => {
	let resizeSettings = resize;

	if (!disableAutoGravity) {
		resizeSettings = resizeSettings.gravity(autoGravity());
	} else {
		const transformationArray = transformation.split(",");
		const gravity = transformationArray?.filter(tItem => tItem.startsWith("g_")) || [];

		if (gravity.length) {
			resizeSettings = resizeSettings.gravity(compass(gravity[0].replace("g_", "")));
		} else {
			resizeSettings = resizeSettings.gravity(autoGravity());
		}
	}

	return resizeSettings;
};

const genSrcSets = (
	image,
	transformations,
	namedTransformation,
	aspectRatio,
	disableAutoGravity,
	maxWidth
) => {
	const output = [];

	for (let i = 0; i < BreakPoints.length; i++) {
		if (maxWidth <= BreakPoints[i + 1]) {
			continue;
		}

		const { height, width } = calculateDimensions(
			image,
			BreakPoints[i],
			transformations,
			aspectRatio
		);

		let resize = fill().width(width).height(height);
		resize = configureGravity(resize, disableAutoGravity, transformations);

		const _image = createCloudinaryImage(image?.public_id, image?.baseUrl)
			.namedTransformation(namedTransformation ?? undefined)
			.resize(resize)
			.format("auto")
			.quality("auto")
			.toURL();

		let mediaString;
		if (i === 0) {
			mediaString = `(min-width:${BreakPoints[i + 1] + 1}px)`;
		} else {
			mediaString = `(max-width: ${width}px)`;
		}

		output.push({ srcSet: `${_image} ${BreakPoints[i]}w`, media: mediaString });
	}
	return output;
};

const cloudinaryGenURL = ({
	cloudinaryImage,
	maxWidth = 800,
	sizes,
	title,
	desc,
	aspectRatio: providedAspectRatio,
	// https://cloudinary.com/documentation/resizing_and_cropping#automatic_cropping_g_auto
	disableAutoGravity,
	transformations: customTransformations,
	baseUrl,
	format = "auto",
}) => {
	const transformations = customTransformations || cloudinaryImage?.raw_transformation || "c_lfill";

	const strippedNamedTransformation = cloudinaryImage?.raw_transformation?.split("/")[0] ?? "";
	const { height, width, aspectRatio } = calculateDimensions(
		cloudinaryImage,
		maxWidth,
		transformations,
		providedAspectRatio
	);

	let resize = fill().width(width).height(height);
	resize = configureGravity(resize, disableAutoGravity, transformations);

	const image = createCloudinaryImage(
		cloudinaryImage?.public_id,
		baseUrl ?? cloudinaryImage?.baseUrl
	)
		.namedTransformation(strippedNamedTransformation ?? undefined)
		.resize(resize)
		.format(format)
		.quality("auto");

	const srcSets = genSrcSets(
		cloudinaryImage,
		transformations,
		strippedNamedTransformation,
		aspectRatio,
		disableAutoGravity,
		maxWidth
	);

	return {
		data: {
			aspectRatio: aspectRatio || providedAspectRatio,
			src: image.toURL(),
			sources: srcSets,
			srcSet: `${image.toURL()} ${maxWidth}px`,
			sizes:
				sizes || generateSizeString(0, [], maxWidth) || `(max-width: 400px) 50vw, ${maxWidth}px`,
		},
		src: image.toURL(),
		alt: desc || title || "",
		title: title || undefined,
		width,
		height,
	};
};

export { cloudinaryGenURL };
